import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, Rocket, Building2, CheckCircle2 } from 'lucide-react';
import posthog from 'posthog-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedSection, BorderBeam } from '@/components/ui';
import { PLAN_LIMITS } from '@/services/subscriptionService';
import { getStripeUrl } from '@/lib/stripe-links';
import { supabase } from '@/lib/supabase';

export const Pricing = () => {
  const navigate = useNavigate();

  // CTA handler for Stripe-backed tiers.
  // A raw Payment Link carries no client_reference_id, so a purchase can't be
  // attributed to a user (webhook would 400 / silently fail). To prevent
  // paid-but-unprovisioned checkouts: logged-in users get client_reference_id
  // appended; logged-out users are routed to sign up first (they can then
  // subscribe from Billing, which is attributed). This supersedes decision D-09.
  const handleStripeClick = async (plan: 'starter' | 'business') => {
    posthog.capture('pricing_plan_selected', { plan });
    const url = getStripeUrl(plan);
    if (!url) {
      navigate('/pricing');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate(`/auth?mode=signup&plan=${plan}`);
      return;
    }

    try {
      const checkoutUrl = new URL(url);
      checkoutUrl.searchParams.set('client_reference_id', user.id);
      window.location.href = checkoutUrl.toString();
    } catch {
      window.location.href = url;
    }
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      Icon: Sparkles,
      description: 'Try it before you commit',
      bestFor: 'Best if you want to see how it works before pointing it at real customers.',
      monthlyPrice: 0,
      popular: false,
      features: [
        'Explore the full dashboard',
        'Try the product',
        'Community support',
      ],
      ctaLabel: 'Get started free',
      ariaLabel: 'Get started with the Free plan',
      handleClick: () => {
        posthog.capture('pricing_plan_selected', { plan: 'free' });
        navigate('/auth?mode=signup');
      },
    },
    {
      id: 'starter',
      name: 'Starter',
      Icon: Zap,
      description: 'For small teams and solo operators',
      bestFor: 'Best if you get under ~50 support questions a week on one website.',
      monthlyPrice: 9.99,
      popular: false,
      features: [
        `${PLAN_LIMITS.starter.bots} AI chatbot`,
        `${PLAN_LIMITS.starter.documents} knowledge documents`,
        `${PLAN_LIMITS.starter.totalChars.toLocaleString()} characters of context`,
        'Unlimited conversations',
        'Email support',
      ],
      ctaLabel: 'Start Starter',
      ariaLabel: 'Start the Starter plan',
      handleClick: () => handleStripeClick('starter'),
    },
    {
      id: 'business',
      name: 'Business',
      Icon: Rocket,
      description: 'For growing teams and multi-site support',
      bestFor: 'Best if answering customers eats an hour+ of your day, or you run more than one site.',
      monthlyPrice: 19.99,
      popular: true,
      features: [
        `${PLAN_LIMITS.business.bots} AI chatbots`,
        `${PLAN_LIMITS.business.documents} knowledge documents`,
        `${PLAN_LIMITS.business.totalChars.toLocaleString()} characters of context`,
        `${PLAN_LIMITS.business.scrapesPerMonth} website scrapes/month`,
        'Unlimited conversations',
        'Custom branding',
        'Priority support',
      ],
      ctaLabel: 'Start Business',
      ariaLabel: 'Start the Business plan — most popular',
      handleClick: () => handleStripeClick('business'),
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      Icon: Building2,
      description: 'Custom solutions for large-scale deployments',
      bestFor: 'Best if you need custom limits, white-labeling, or a signed SLA.',
      monthlyPrice: 0,
      popular: false,
      features: [
        'Unlimited chatbots',
        'Unlimited conversations',
        'Custom context limits',
        'White-label options',
        'Dedicated support',
        'SLA guarantee',
      ],
      ctaLabel: 'Contact us',
      ariaLabel: 'Contact us for Enterprise pricing',
      handleClick: () => navigate('/contact'),
    },
  ];

  return (
    <section id="pricing" className="py-16 md:py-24">
      <div className="container">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-foreground tracking-tight mb-4">
              Simple pricing, powerful results
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Start free. Scale as your support volume grows. No contracts, cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {tiers.map((tier) => (
              <Card
                key={tier.id}
                className={`relative flex flex-col h-full overflow-hidden transition-all duration-300 ${
                  tier.popular
                    ? 'border-primary shadow-lg bg-card hover:shadow-xl hover:shadow-primary/15 hover:-translate-y-1'
                    : 'border-border bg-card shadow-sm hover:shadow-md hover:shadow-primary/8 hover:-translate-y-0.5 hover:border-primary/20'
                }`}
              >
                {tier.popular && (
                  <BorderBeam duration={8} colorFrom="hsl(var(--primary))" colorTo="hsl(var(--accent-foreground))" borderWidth={1.5} />
                )}
                {tier.popular && (
                  <div className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest text-center py-1">
                    Most Popular
                  </div>
                )}

                <CardHeader className="text-center pb-4 pt-8">
                  <div className="flex justify-center mb-4">
                    <tier.Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="font-display text-xl font-bold text-foreground mb-1">
                    {tier.name}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{tier.description}</p>
                  <div className="mt-4" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {tier.monthlyPrice > 0 ? (
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="font-display text-4xl font-bold text-foreground">
                          ${tier.monthlyPrice.toFixed(2)}
                        </span>
                        <span className="text-sm font-normal text-muted-foreground">
                          /mo
                        </span>
                      </div>
                    ) : tier.id === 'enterprise' ? (
                      <span className="font-display text-4xl font-bold text-foreground">Custom</span>
                    ) : (
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="font-display text-4xl font-bold text-foreground">$0</span>
                        <span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex flex-col flex-1 space-y-6 px-6 pb-6">
                  <p className="text-xs leading-relaxed text-foreground/70 bg-muted/50 rounded-lg px-3 py-2 text-center">
                    {tier.bestFor}
                  </p>
                  <ul className="flex-1 space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={tier.popular ? 'cta' : 'secondary'}
                    className="w-full h-11"
                    onClick={tier.handleClick}
                    aria-label={tier.ariaLabel}
                  >
                    {tier.ctaLabel}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
